import { useTranslation } from 'react-i18next';
import Button from '@components/atoms/ui-kit/button.tsx';
import { useDeleteUserAccount } from '@utils/api/users/use-delete-user-account.tsx';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { BaseError } from '@utils/types/shared/base.ts';
import { AxiosError } from 'axios';
import { useDeactivateUserAccount } from '@utils/api/users/use-deactivate-user-account.tsx';
import { useDeleteOrganizationAccount } from '@utils/api/organizations/use-delete-organization-account.tsx';
import { useOrganization } from '@utils/api/users/use-get-organization.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import { useGetMe } from '@utils/api/users/use-get-me.tsx';
import PageHeader from '@components/layout/page-header.tsx';
import { Text } from '@components/atoms/ui-kit/text';

const DeleteAccountSettings = () => {
  const { t } = useTranslation();
  const { mutate: deleteAccount } = useDeleteUserAccount();
  const { mutate: deactivateAccount } = useDeactivateUserAccount();
  const { mutate: deleteOrganizationAccount } = useDeleteOrganizationAccount();
  const [deactivateAccountConfirmation, setDeactivateAccountConfirmation] =
    useState(false);
  const [deleteAccountConfirmation, setDeleteAccountConfirmation] =
    useState(false);
  const [deleteOrgAccountConfirmation, setDeleteOrgAccountConfirmation] =
    useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletingOrg, setIsDeletingOrg] = useState(false);
  const { data: organization } = useOrganization();
  const { data: me } = useGetMe();

  // Check if user has admin or sysop role
  const userRoles = (me?.result?.roles ?? []) as unknown as string[];
  const isAdmin = userRoles.includes('admin') || userRoles.includes('sysop');

  const handleDeactivateAccountConfirm = () => {
    setIsDeactivating(true);
    deactivateAccount(
      {},
      {
        onSuccess: () => {
          toast.success(t('account-deactivated-successfully'));
          setDeactivateAccountConfirmation(false);
          setIsDeactivating(false);
        },
        onError: (err: AxiosError<BaseError>) => {
          toast.error(
            t('error-occurred-while-deactivating-account', {
              error: err?.response?.data?.error_description || err.message || ''
            })
          );
          setIsDeactivating(false);
        }
      }
    );
  };

  const handleDeleteConfirmation = async () => {
    setIsDeleting(true);

    await new Promise(resolve => setTimeout(resolve, 300));

    deleteAccount(
      {},
      {
        onSuccess: () => {
          toast.success(t('account-deleted-successfully'));
          setDeleteAccountConfirmation(false);
          setIsDeleting(false);
        },
        onError: (err: AxiosError<BaseError>) => {
          toast.error(
            t('error-occurred-while-deleting-account', {
              error:
                err?.response?.data?.error_description ||
                err?.response?.data?.message ||
                err?.message ||
                ''
            })
          );
          setIsDeleting(false);
        }
      }
    );
  };

  const handleDeleteOrganizationConfirmation = async () => {
    if (!organization || !organization.id) {
      return;
    }

    setIsDeletingOrg(true);

    await new Promise(resolve => setTimeout(resolve, 300));

    deleteOrganizationAccount(organization.id, {
      onSuccess: () => {
        toast.success(t('account-deleted-successfully'));
        setDeleteOrgAccountConfirmation(false);
        setIsDeletingOrg(false);
      },
      onError: (err: AxiosError<BaseError>) => {
        toast.error(
          t('error-occurred-while-deleting-account', {
            error: err?.response?.data?.error_description || err.message || ''
          })
        );
        setIsDeletingOrg(false);
      }
    });
  };

  return (
    <div className="rounded-2xl border bg-app-soft shadow-card p-4 sm:p-6 mt-4 border-red-600 dark:border-red-400">
      <PageHeader
        title={
          <Text as="span" tone="danger">
            {t('danger-zone')}
          </Text>
        }
        caption={t('user-account-settings')}
        className="border-b-0 mb-4"
        contentClassName="px-0 py-0"
      />
      <div className="flex flex-wrap items-start gap-2">
        <Button
          label={t('deactivate-account')}
          size="small"
          variant="danger"
          onClick={() => setDeactivateAccountConfirmation(true)}
        />
        <Button
          label={t('delete-account')}
          size="small"
          variant="danger"
          onClick={() => setDeleteAccountConfirmation(true)}
        />
      </div>
      {isAdmin && (
        <div className="mt-6 pt-6 border-t border-hairline">
          <Text as="p" weight="medium" className="mb-3">
            {t('organization-account-settings')}
          </Text>
          <Button
            label={t('delete-organization')}
            size="small"
            variant="danger"
            onClick={() => setDeleteOrgAccountConfirmation(true)}
          />
        </div>
      )}
      <Modal
        isOpen={deactivateAccountConfirmation}
        onClose={() => setDeactivateAccountConfirmation(false)}
        size="small"
        title={t('deactivate-account-confirmation')}
        caption={t(
          'are-you-sure-you-want-to-deactivate-your-account-you-will-not-be-able-to-recover-it-without-contacting-support'
        )}
        footerActions={[
          <Button
            onClick={() => setDeactivateAccountConfirmation(false)}
            label={t('cancel')}
            size={'small'}
            variant={'smoke'}
            disabled={isDeactivating}
          />,
          <Button
            type={'button'}
            onClick={() => {
              setDeactivateAccountConfirmation(false);
              handleDeactivateAccountConfirm();
            }}
            label={isDeactivating ? t('deactivating') : t('deactivate')}
            size={'small'}
            variant={'primary'}
            disabled={isDeactivating}
          />
        ]}
        children={undefined}
      />{' '}
      <Modal
        isOpen={deleteAccountConfirmation}
        onClose={() => setDeleteAccountConfirmation(false)}
        size="small"
        title={t('delete-account-confirmation')}
        caption={t(
          'are-you-sure-you-want-to-delete-your-account-this-action-cannot-be-undone'
        )}
        footerActions={[
          <Button
            onClick={() => setDeleteAccountConfirmation(false)}
            label={t('cancel')}
            size={'small'}
            variant={'smoke'}
            disabled={isDeleting}
          />,
          <Button
            type={'button'}
            onClick={handleDeleteConfirmation}
            label={isDeleting ? t('deleting') : t('delete')}
            size={'small'}
            variant={'primary'}
            disabled={isDeleting}
          />
        ]}
        children={undefined}
      />{' '}
      <Modal
        isOpen={deleteOrgAccountConfirmation}
        onClose={() => setDeleteOrgAccountConfirmation(false)}
        size="small"
        title={t('delete-organization-account-confirmation')}
        caption={t(
          'are-you-sure-you-want-to-delete-your-organization-account-this-action-will-delete-everything-in-your-account-and-other-user-accounts-in-this-organization-and-this-action-cannot-be-undone'
        )}
        footerActions={[
          <Button
            onClick={() => setDeleteOrgAccountConfirmation(false)}
            label={t('cancel')}
            size={'small'}
            variant={'smoke'}
            disabled={isDeletingOrg}
          />,
          <Button
            type={'button'}
            onClick={handleDeleteOrganizationConfirmation}
            label={isDeletingOrg ? t('deleting') : t('delete')}
            size={'small'}
            variant={'primary'}
            disabled={isDeletingOrg}
          />
        ]}
        children={undefined}
      />{' '}
    </div>
  );
};

export default DeleteAccountSettings;
